import { useEffect, useState } from 'react';
import SideNavBar from '../components/SideNavBar';
import { createType, deleteType, getErrorMessage, getTypes, updateType } from '../services/authApi';

function PaymentTypePage({ user, onLogout }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [formError, setFormError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingType, setDeletingType] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: ''
  });

  const loadTypes = async () => {
    try {
      const data = await getTypes();
      setTypes(Array.isArray(data) ? data : data?.data || []);
      setPageError('');
    } catch (requestError) {
      setPageError(getErrorMessage(requestError, 'Failed to load payment types.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTypes();
  }, []);

  const openCreateModal = () => {
    setEditingType(null);
    setFormData({ name: '' });
    setFormError('');
    setIsModalOpen(true);
  };

  const openEditModal = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name || '',
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const closeModal = (forceClose = false) => {
    if (isSubmitting && !forceClose) {
      return;
    }

    setIsModalOpen(false);
    setEditingType(null);
    setFormData({ name: '' });
    setFormError('');
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
      };

      if (editingType) {
        const updatedType = await updateType(editingType.id, payload);
        setTypes((prev) =>
          prev.map((current) => (current.id === editingType.id ? updatedType : current))
        );
      } else {
        const createdType = await createType(payload);
        setTypes((prev) => [createdType, ...prev]);
      }

      closeModal(true);
    } catch (requestError) {
      setFormError(
        getErrorMessage(
          requestError,
          editingType ? 'Failed to update payment type.' : 'Failed to create payment type.'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteModal = (type) => {
    setDeletingType(type);
    setDeleteError('');
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = (forceClose = false) => {
    if (isDeleting && !forceClose) {
      return;
    }

    setIsDeleteModalOpen(false);
    setDeletingType(null);
    setDeleteError('');
  };

  const handleDeleteConfirm = async () => {
    if (!deletingType) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteType(deletingType.id);
      setTypes((prev) => prev.filter((current) => current.id !== deletingType.id));
      closeDeleteModal(true);
    } catch (requestError) {
      setDeleteError(getErrorMessage(requestError, 'Failed to delete payment type.'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-maroon-50 to-maroon-100 md:flex-row">
      <SideNavBar user={user} onLogout={onLogout} />
      <div className="flex flex-1 p-6">
        <div className="w-full rounded-2xl bg-white p-6 shadow-xl shadow-maroon-900/10">
          <div className="mb-5 flex items-center justify-between">
            <h1 className="text-3xl font-bold text-maroon-900">Payment Types</h1>
            <button
              type="button"
              onClick={openCreateModal}
              className="rounded-lg bg-maroon-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-maroon-800"
            >
              Create Payment Type
            </button>
          </div>

          {loading ? <p className="text-maroon-700">Loading payment types...</p> : null}
          {pageError ? <p className="font-medium text-red-600">{pageError}</p> : null}

          {!loading && !pageError ? (
            <div className="overflow-x-auto rounded-lg border border-maroon-100">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-maroon-50 text-maroon-900">
                  <tr>
                    <th className="px-4 py-3 font-semibold">ID</th>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {types.length === 0 ? (
                    <tr>
                      <td className="px-4 py-3 text-maroon-700" colSpan={3}>
                        No payment types found.
                      </td>
                    </tr>
                  ) : (
                    types.map((type, index) => (
                      <tr key={type.id || index} className="border-t border-maroon-100">
                        <td className="px-4 py-3 text-maroon-800">{index+1}</td>
                        <td className="px-4 py-3 text-maroon-800">
                          {type.name || type.type_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-maroon-800">
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => openEditModal(type)}
                              className="rounded-md bg-amber-500 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-600"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => openDeleteModal(type)}
                              className="rounded-md bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>

      {isModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h2 className="mb-4 text-2xl font-bold text-maroon-900">
              {editingType ? 'Edit Payment Type' : 'Create Payment Type'}
            </h2>
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <label className="text-sm font-medium text-maroon-900" htmlFor="type_name">
                Name
              </label>
              <input
                id="type_name"
                type="text"
                value={formData.name}
                onChange={(event) => handleFieldChange('name', event.target.value)}
                className="rounded-lg border border-maroon-200 px-3 py-2 outline-none ring-maroon-300 focus:ring-2"
                required
              />

              {formError ? <p className="text-sm font-medium text-red-600">{formError}</p> : null}

              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={isSubmitting}
                  className="rounded-lg border border-maroon-300 px-4 py-2 text-sm font-semibold text-maroon-800 hover:bg-maroon-50 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-lg bg-maroon-700 px-4 py-2 text-sm font-semibold text-white hover:bg-maroon-800 disabled:opacity-60"
                >
                  {isSubmitting ? 'Saving...' : editingType ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {isDeleteModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h2 className="mb-2 text-2xl font-bold text-maroon-900">Delete Payment Type</h2>
            <p className="mb-4 text-sm text-maroon-700">
              Are you sure you want to delete "{deletingType?.name || 'this item'}"?
            </p>

            {deleteError ? <p className="mb-3 text-sm font-medium text-red-600">{deleteError}</p> : null}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={isDeleting}
                className="rounded-lg border border-maroon-300 px-4 py-2 text-sm font-semibold text-maroon-800 hover:bg-maroon-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PaymentTypePage;
